import { ConfirmMutation, ResendConfirmationMutation } from "gql/mutations/auth.graphql";
import { MessageBar, MessageBarType, PrimaryButton, TextField, DefaultButton } from "office-ui-fabric-react";
import React, { useState, useEffect } from "react";
import { gqlMutation } from "../common/lib";
import { LoginLayout } from "./LoginLayout";
import { useLocation } from "react-router-dom";
import { PasswordRequirements, validatePassword } from "./common";

const joinBr = (array: any[]) => array.reduce((all, cur) => [...all, <br />, cur]);

const ConfirmationPage = () => {
  const [originalErrors, setOriginalErrors] = useState(window.data?.error ? [window.data.error] : []);
  const [errors, setErrors] = useState<String[]>([]);
  const [terminalError] = useState(Boolean(window.data?.error));

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [redirectTo, setRedirectTo] = useState("/");

  const [email, setEmail] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [tokenRequestSuccess, setTokenRequestSuccess] = useState(false);
  const [requestTokenCountdown, setRequestTokenCountdown] = useState(0);
  const [canRequestNewToken, setCanRequestNewToken] = useState(true);
  const [redirectCountdown, setRedirectCountdown] = useState(10);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const uid = Number(searchParams.get("uid"));

  let isInvalidToken = false;
  let userAlreadyConfirmed = false;
  originalErrors.forEach(e => {
    if (e.toLowerCase().includes("invalid confirmation token")) {
      isInvalidToken = true;
      return;
    }

    if (e.toLowerCase().includes("user is already confirmed")) {
      userAlreadyConfirmed = true;
    }
  });

  // Countdown timer for token request cooldown
  useEffect(() => {
    if (requestTokenCountdown > 0) {
      const timer = setTimeout(() => {
        setRequestTokenCountdown((prev) => {
          if (prev <= 1) {
            setCanRequestNewToken(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [requestTokenCountdown]);

  // Countdown timer for redirect when user is already confirmed
  useEffect(() => {
    if (userAlreadyConfirmed && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            window.location.href = "/users/sign_in";
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [userAlreadyConfirmed, redirectCountdown]);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleRequestNewToken = (mutateFunc) => {
    if (isNaN(uid)) {
      setErrors(["Invalid UID"]);
      return;
    }

    if (!isValidEmail(email)) {
      setErrors(["Please enter a valid email address"]);
      return;
    }

    setCanRequestNewToken(false);
    setRequestTokenCountdown(30);

    mutateFunc({ variables: { uid, email } })
      .then(({ data }) => {
        setTokenRequestSuccess(true);
        setShowEmailInput(false);
        setEmail("");
        setErrors([]);
      })
      .catch((res) => {
        const errorMessage = res.graphQLErrors?.[0]?.message
          ? JSON.parse(res.graphQLErrors[0].message)[0]
          : "Failed to send confirmation email";
        setErrors([errorMessage]);
        setCanRequestNewToken(true);
        setRequestTokenCountdown(0);
      });
  };

  const handleSubmit = (mutateFunc) => {
    setErrors([]);
    const token = window.data.token;

    mutateFunc({ variables: { password, token } }).then(({ data }) => {
      data = data.reset_password;
      if (data.success) {
        setSuccess(true);
        setRedirectTo(data.redirect_to);
      } else {
        setErrors(data.errors ?? []);
      }
    });
  };

  const renderSuccess = () => {
    const redirect = () => (window.location.href = redirectTo);
    window.setTimeout(redirect, 0);
    return (
      <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
        Account activated successfully.
      </MessageBar>
    );
  };

  const renderAlreadyConfirmed = () => {
    return (
      <div>
        <h1>Account Already Activated</h1>
        <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
          Your account is already activated. You can now log in with your credentials.
        </MessageBar>

        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <p style={{ marginBottom: "20px", fontSize: "14px", color: "#605e5c" }}>
            Redirecting to login page in {redirectCountdown} seconds...
          </p>
          <PrimaryButton
            onClick={() => window.location.href = "/users/sign_in"}
            data-testid="redirect-to-login-btn"
          >
            Go to Login Now
          </PrimaryButton>
        </div>
      </div>
    );
  };

  const canSubmit = () => {
    return (
      !!password &&
      password === confirmPassword &&
      validatePassword(password)
    );
  };

  const renderForm = (mutateFunc) => {
    return (
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(mutateFunc); }}>
        <div>
          <TextField
            name="user_password"
            label="Password"
            type="password"
            value={password}
            autoFocus
            onChange={(e, v) => setPassword(v ?? "")}
            data-testid="password"
          />
          <TextField
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e, v) => setConfirmPassword(v ?? "")}
            data-testid="confirm-password"
          />
        </div>
        <div className="text-center">
          <PrimaryButton data-testid="submit" type="submit" onClick={() => handleSubmit(mutateFunc)} disabled={!canSubmit()}>
            Submit
          </PrimaryButton>
        </div>
      </form>
    );
  };

  const renderInvalidToken = () => {
    return (
      <div>
        <h1>Activate Your Account</h1>
        {!tokenRequestSuccess && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            The confirmation token is invalid or has expired.
          </MessageBar>
        )}

        {showEmailInput && (
          <div style={{ marginTop: "20px" }}>
            <TextField
              label="Enter your email address"
              value={email}
              onChange={(e, v) => setEmail(v ?? "")}
              placeholder="user@example.com"
              data-testid="email-input"
            />
          </div>
        )}
        {errors.length > 0 && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            {joinBr(errors)}
          </MessageBar>
        )}

        {tokenRequestSuccess && (
          <MessageBar messageBarType={MessageBarType.success}>
            Account activation email sent. Check your inbox for instructions.
          </MessageBar>
        )}

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          {gqlMutation(ResendConfirmationMutation, (mutateFunc) => (
            <>
              {!showEmailInput && canRequestNewToken
                ? (
                <PrimaryButton
                  onClick={() => setShowEmailInput(true)}
                  data-testid="request-new-token-btn"
                >
                  Resend Activation Email
                </PrimaryButton>
                  )
                : showEmailInput && canRequestNewToken
                  ? (
                <div>
                  <PrimaryButton
                    onClick={() => handleRequestNewToken(mutateFunc)}
                    disabled={!email}
                    data-testid="send-activation-link-btn"
                    style={{ marginRight: "10px" }}
                  >
                    Send Activation Link
                  </PrimaryButton>
                  <DefaultButton
                    onClick={() => {
                      setShowEmailInput(false);
                      setEmail("");
                    }}
                    style={{ padding: 0 }}
                  >
                    Cancel
                  </DefaultButton>
                </div>
                    )
                  : (
                <div>
                  <p>You can request a new activation link in {requestTokenCountdown} seconds</p>
                  <DefaultButton data-testid="request-new-link-btn" disabled>
                    Request New Link ({requestTokenCountdown}s)
                  </DefaultButton>
                </div>
                    )}
            </>
          ))}
        </div>
      </div>
    );
  };

  const showForm = !terminalError && !success && !isInvalidToken && !userAlreadyConfirmed;

  return (
    <LoginLayout withLinkedIn={false}>
      <div className="h-100 flex items-center justify-center login-panel">
        <div className="w-360px min-h-500px p-16 flex flex-col">
          <div className="text-32 text-darkgray">
            Welcome to Crosslake's
            <br />
            Corsis&#0174; application!
          </div>
          {gqlMutation(ConfirmMutation, (mutateFunc) => (
            <div className="flex-1 overflow-hidden">
              {userAlreadyConfirmed
                ? (
                    renderAlreadyConfirmed()
                  )
                : isInvalidToken
                  ? (
                      renderInvalidToken()
                    )
                  : (
                <>
                  <h1>Activate Your Account</h1>
                  <PasswordRequirements password={password} />
                  {errors.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                      {joinBr(errors)}
                    </MessageBar>
                  )}
                  {success && renderSuccess()}
                  {showForm && renderForm(mutateFunc)}
                </>
                    )}
            </div>
          ))}
        </div>
      </div>
    </LoginLayout>
  );
};

export default ConfirmationPage;
